//The simple lend vault apy component which is used for stats

import { useVaultApy, LendingVaultExpanded } from "@bridgesplit/abf-react";
import { StatColumn, StatProps, Tooltip, Text } from "@bridgesplit/ui";
import { filterUndefined, formatPercent } from "@bridgesplit/utils";

import { TooltipContent } from "./common";
import { BaseApy } from "./BaseApy";
import { useExternalRewardStats } from "./RewardsLendVaultApy";

export function RewardsLendVaultSimpleApy({
    lendingVaultExpanded,
    depositAmount = 0
}: {
    lendingVaultExpanded: LendingVaultExpanded | undefined;
    depositAmount?: number;
}) {
    const preDepositApy = useVaultApy({ lendingVaultExpanded, includeRewards: true });
    const postDepositApy = useVaultApy({ lendingVaultExpanded, depositAmount, includeRewards: true });
    const postDepositBaseApy = useVaultApy({ lendingVaultExpanded, depositAmount, includeRewards: false });

    const apys = [preDepositApy, postDepositApy].filter(filterUndefined);

    return (
        <Tooltip
            placement="right"
            title={
                <RewardsLendVaultSimpleTooltip
                    lendingVaultExpanded={lendingVaultExpanded}
                    apy={postDepositBaseApy}
                    depositAmount={depositAmount}
                />
            }
            reverseColors
        >
            <VaultRateDisplay apys={apys} />
        </Tooltip>
    );
}

function RewardsLendVaultSimpleTooltip({
    lendingVaultExpanded,
    apy,
    depositAmount = 0
}: {
    lendingVaultExpanded: LendingVaultExpanded | undefined;
    apy: number | undefined;
    depositAmount?: number;
}) {
    const externalRewardStats = useExternalRewardStats({ lendingVaultExpanded, depositAmount });

    return (
        <TooltipContent>
            <BaseApy apy={apy} additionalStats={externalRewardStats} />
        </TooltipContent>
    );
}

function VaultRateDisplay({ apys }: { apys: number[] | undefined }) {
    const stats: StatProps[] = [
        {
            value: <VaultDisplayApy apy={apys} />,
            caption: ""
        }
    ];

    return <StatColumn stats={stats} captionVariant="body1" />;
}

function VaultDisplayApy({ apy }: { apy: number[] | undefined }) {
    if (!apy || apy.length === 0) return null;

    const isRangeApy = apy.length > 1;

    const displayApyDelta = isRangeApy && formatPercent(apy[apy.length - 1]) !== formatPercent(apy[0]) ? true : false;

    const stats: StatProps[] = [
        {
            value: displayApyDelta ? (
                [<VaultApyStat apy={apy[0]} />, <VaultApyStat apy={apy[apy.length - 1]} underline />]
            ) : (
                <VaultApyStat apy={apy[0]} />
            ),
            caption: ""
        }
    ];

    return <StatColumn stats={stats} captionVariant="body1" />;
}
function VaultApyStat({ apy, underline = false }: { apy: number | undefined; underline?: boolean }) {
    if (!apy) return null;

    return (
        <Text svgColor="disabled" underline={underline} sx={{ display: "inline" }}>
            {formatPercent(apy)}
        </Text>
    );
}
